import { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DocumentsSection, { buildDefaultDocFolders } from './DocumentsSection';
import { computeEquipmentUsage, getCats, unitOf, serviceQty } from './EquipmentPage';

// ─── Constants ─────────────────────────────────────────────────────────────────

const PROJECT_TYPES   = ['Crewing', 'Dry hire', 'Permanent installation'];
const STATUSES        = ['Planning', 'Confirmed', 'Active', 'On Hold', 'Completed', 'Canceled'];
const FORM_CURRENCIES = ['EUR', 'USD', 'GBP', 'UAH', 'AED', 'SAR', 'KWD', 'QAR'];
// Окремий вимір від основного статусу — стан оплати, тому множинний вибір
// (проєкт може бути одночасно Active і Waiting for Payment).
const PAYMENT_STATUSES = ['Waiting for Payment', 'Payment for Supplier'];
// Equipment Checking стосується лише Dry hire (перевірка обладнання перед
// видачею клієнту) — для Crewing/Permanent installation цей пункт нерелевантний.
const DRY_HIRE_PAYMENT_STATUSES = ['Equipment Checking'];

const mockCompanies  = [];

const PERSON_ROLES = [
  'Project Manager', 'Event Coordinator', 'HR', 'Finance',
  'Head of Lighting', 'Head of Video', 'Head of Rigging', 'Other',
];

// ─── Styles ────────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'var(--border)', border: '1px solid var(--border-strong)',
  borderRadius: '8px', color: 'var(--text-primary)', fontSize: '14px',
  boxSizing: 'border-box', outline: 'none',
};

const labelStyle = {
  color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '1px', marginBottom: '6px', display: 'block',
};

const sectionStyle = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: '12px', padding: '24px', marginBottom: '16px',
};

const sectionTitle = {
  color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '2px', margin: '0 0 20px 0',
};

// ─── Finance Helpers ───────────────────────────────────────────────────────────

const emptyFine    = () => ({ id: Date.now() + Math.random(), amount: '', comment: '' });
const emptyPayment = () => ({ id: Date.now() + Math.random(), amount: '', date: '', comment: '' });
const emptyPerson  = () => ({ role: '', name: '', phone: '', email: '', visibleToFreelancers: false });

// Дата → 'YYYY-MM-DD' за МІСЦЕВИМ часом. НЕ використовувати toISOString():
// вона конвертує в UTC і для UTC+3/+4 зсуває дату на день назад.
const toDateStr = d => d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : '';

// Усі дати (YYYY-MM-DD) в межах [fromStr, toStr], включно — для перемикача
// travel/working днів по кожному конкретному дню періоду (04.08.2026).
const datesInRange = (fromStr, toStr) => {
  if (!fromStr || !toStr || fromStr > toStr) return [];
  const out = [];
  const cur = new Date(fromStr + 'T00:00:00');
  const end = new Date(toStr + 'T00:00:00');
  while (cur <= end) {
    out.push(toDateStr(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const n           = v  => parseFloat(v)  || 0;
const sumFines    = arr => arr.reduce((s, f) => s + n(f.amount), 0);
const sumPayments = arr => arr.reduce((s, p) => s + n(p.amount), 0);

const defaultCrewingFinance = () => ({
  income: {
    projectPrice:  { amount: '', comment: '' },
    crewExpenses:  { amount: '', comment: '' },
    crewOvertimes: { amount: '', comment: '' },
    fines: [],
  },
  clientPayments: [],
  freelancerPayments: {
    planned:       { amount: '', comment: '' },
    crewExpenses:  { amount: '', comment: '' },
    crewOvertimes: { amount: '', comment: '' },
    fines: [],
    paid:  [],
  },
});

const defaultPermInstallFinance = () => ({
  income: {
    equipmentCost:    { amount: '', comment: '' },
    installationCost: { amount: '', comment: '' },
    fines:            [],
  },
  clientPayments: [],
  supplierPayments: {
    planned:           { amount: '', comment: '' },
    paymentForDamages: { amount: '', comment: '' },
    fines:             [],
    paid:              [],
  },
  freelancerPayments: {
    planned: { amount: '', comment: '' },
    fines:   [],
    paid:    [],
  },
});

const defaultDryHireFinance = () => ({
  income: {
    projectPrice:      { amount: '', comment: '' },
    projectExpenses:   { amount: '', comment: '' },
    paymentForDamages: { amount: '', comment: '' },
    fines: [],
  },
  clientPayments: [],
  supplierPayments: {
    planned:           { amount: '', comment: '' },
    fines:             [],
    paymentForDamages: { amount: '', comment: '' },
    paid:              [],
  },
});

const computeFinanceSummary = (finance, type) => {
  if (type === 'Crewing') {
    const d      = finance.crewing;
    const income = n(d.income.projectPrice.amount) + n(d.income.crewExpenses.amount) + n(d.income.crewOvertimes.amount) - sumFines(d.income.fines);
    const fp     = d.freelancerPayments;
    const outcome = n(fp.planned.amount) + n(fp.crewExpenses.amount) + n(fp.crewOvertimes.amount) - sumFines(fp.fines);
    return { income, outcome, profit: income - outcome };
  }
  if (type === 'Permanent installation') {
    const d       = finance.permInstall;
    const income  = n(d.income.installationCost.amount) + n(d.income.equipmentCost.amount) - sumFines(d.income.fines);
    const sp      = d.supplierPayments;
    const fp      = d.freelancerPayments;
    const outSup  = n(sp.planned.amount) + n(sp.paymentForDamages.amount) - sumFines(sp.fines);
    const outFree = n(fp.planned.amount) - sumFines(fp.fines);
    return { income, outcome: outSup + outFree, profit: income - outSup - outFree };
  }
  if (type === 'Dry hire') {
    const d      = finance.dryHire;
    const income = n(d.income.projectPrice.amount) + n(d.income.projectExpenses.amount) + n(d.income.paymentForDamages.amount) - sumFines(d.income.fines);
    const sp     = d.supplierPayments;
    const outcome = n(sp.planned.amount) + n(sp.paymentForDamages.amount) - sumFines(sp.fines);
    return { income, outcome, profit: income - outcome };
  }
  return { income: 0, outcome: 0, profit: 0 };
};

// deep-path updater: set('income.projectPrice.amount', val)
function makeUpdater(onChange) {
  return (path, val) => {
    onChange(prev => {
      const parts = path.split('.');
      const clone = JSON.parse(JSON.stringify(prev));
      let obj = clone;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = val;
      return clone;
    });
  };
}

// ─── Finance UI atoms ──────────────────────────────────────────────────────────

function FinanceDivider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 16px' }}>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
      <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
    </div>
  );
}

function TotalRow({ label, value, cur, color }) {
  const fmt = v => `${cur} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 18px', margin: '8px 0 16px' }}>
      <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</span>
      <span style={{ color: color || 'var(--info-text)', fontSize: '20px', fontWeight: 'bold' }}>{fmt(value)}</span>
    </div>
  );
}

function AmountField({ label, value, comment, onChange, onCommentChange, cur, hint }) {
  const [showComment, setShowComment] = useState(!!comment);
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
        <span style={{ ...labelStyle, marginBottom: 0 }}>{label}</span>
        {hint && <span style={{ color: 'var(--text-disabled)', fontSize: '10px', fontStyle: 'italic' }}>{hint}</span>}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '12px', pointerEvents: 'none', userSelect: 'none' }}>{cur}</span>
          <input type="number" placeholder="0" value={value} onChange={e => onChange(e.target.value)}
            style={{ ...inputStyle, paddingLeft: `${8 + cur.length * 9}px` }} />
        </div>
        <button type="button" onClick={() => setShowComment(p => !p)} title="Comment"
          style={{ flexShrink: 0, background: showComment ? 'var(--info-bg)' : 'transparent', border: '1px solid ' + (showComment ? 'var(--info-text)' : 'var(--border-strong)'), color: showComment ? 'var(--info-text)' : 'var(--text-disabled)', padding: '0 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
          💬
        </button>
      </div>
      {showComment && (
        <input placeholder="Comment..." value={comment} onChange={e => onCommentChange(e.target.value)}
          style={{ ...inputStyle, marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderColor: 'var(--bg-subtle)' }} />
      )}
    </div>
  );
}

function FinesList({ fines, onChange, cur }) {
  const add    = () => onChange([...fines, emptyFine()]);
  const remove = id  => onChange(fines.filter(f => f.id !== id));
  const update = (id, field, val) => onChange(fines.map(f => f.id === id ? { ...f, [field]: val } : f));
  const total  = sumFines(fines);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ ...labelStyle, marginBottom: 0 }}>Fines / Penalties</span>
          <span style={{ color: 'var(--accent)', fontSize: '10px', fontStyle: 'italic' }}>subtracted from total</span>
        </div>
        {total > 0 && <span style={{ color: 'var(--accent)', fontSize: '12px', fontWeight: 'bold' }}>− {cur} {total.toLocaleString()}</span>}
      </div>
      {fines.map((fine, idx) => (
        <div key={fine.id} style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', borderRadius: '8px', padding: '12px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Fine #{idx + 1}</span>
            <span onClick={() => remove(fine.id)} style={{ color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' }}>Remove</span>
          </div>
          <div style={{ position: 'relative', marginBottom: '6px' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '12px', pointerEvents: 'none' }}>{cur}</span>
            <input type="number" placeholder="0" value={fine.amount} onChange={e => update(fine.id, 'amount', e.target.value)}
              style={{ ...inputStyle, paddingLeft: `${8 + cur.length * 9}px`, background: 'var(--bg-subtle)', borderColor: 'var(--border)' }} />
          </div>
          <input placeholder="Comment..." value={fine.comment} onChange={e => update(fine.id, 'comment', e.target.value)}
            style={{ ...inputStyle, fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderColor: 'var(--border)' }} />
        </div>
      ))}
      <button type="button" onClick={add}
        style={{ background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', width: '100%' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
        + Add Fine / Penalty
      </button>
    </div>
  );
}

function PaidList({ payments, onChange, cur, label = 'Already Paid' }) {
  const add    = () => onChange([...payments, emptyPayment()]);
  const remove = id  => onChange(payments.filter(p => p.id !== id));
  const update = (id, field, val) => onChange(payments.map(p => p.id === id ? { ...p, [field]: val } : p));
  const total  = sumPayments(payments);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ ...labelStyle, marginBottom: 0 }}>{label}</span>
        {total > 0 && <span style={{ color: 'var(--success-text)', fontSize: '12px', fontWeight: 'bold' }}>{cur} {total.toLocaleString()} paid</span>}
      </div>
      {payments.map((pmt, idx) => (
        <div key={pmt.id} style={{ background: 'var(--success-bg)', border: '1px solid var(--success-text)', borderRadius: '8px', padding: '12px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Payment #{idx + 1}</span>
            <span onClick={() => remove(pmt.id)} style={{ color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' }}>Remove</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '12px', pointerEvents: 'none' }}>{cur}</span>
              <input type="number" placeholder="0" value={pmt.amount} onChange={e => update(pmt.id, 'amount', e.target.value)}
                style={{ ...inputStyle, paddingLeft: `${8 + cur.length * 9}px`, background: 'var(--bg-subtle)', borderColor: 'var(--border)' }} />
            </div>
            <input type="date" value={pmt.date} onChange={e => update(pmt.id, 'date', e.target.value)}
              style={{ ...inputStyle, colorScheme: 'var(--color-scheme)', background: 'var(--bg-subtle)', borderColor: 'var(--border)' }} />
          </div>
          <input placeholder="Comment..." value={pmt.comment} onChange={e => update(pmt.id, 'comment', e.target.value)}
            style={{ ...inputStyle, fontSize: '12px', color: 'var(--text-muted)', background: 'var(--bg-subtle)', borderColor: 'var(--border)' }} />
        </div>
      ))}
      <button type="button" onClick={add}
        style={{ background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', width: '100%' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--success-text)'; e.currentTarget.style.color = 'var(--success-text)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
        + Add Payment
      </button>
    </div>
  );
}

// ─── Finance sections per type ─────────────────────────────────────────────────

function CrewingFinance({ data, onChange, cur }) {
  const set = makeUpdater(onChange);
  const i   = data.income;
  const fp  = data.freelancerPayments;

  const totalPrice      = n(i.projectPrice.amount) + n(i.crewExpenses.amount) + n(i.crewOvertimes.amount) - sumFines(i.fines);
  const clientPaid      = sumPayments(data.clientPayments);
  const clientToBePaid  = totalPrice - clientPaid;
  const totalFreelancer = n(fp.planned.amount) + n(fp.crewExpenses.amount) + n(fp.crewOvertimes.amount) - sumFines(fp.fines);
  const freelancerPaid  = sumPayments(fp.paid);
  const freelancerToBe  = totalFreelancer - freelancerPaid;
  const profit          = totalPrice - totalFreelancer;

  return (
    <>
      {/* Income */}
      <FinanceDivider label="Income" />
      <AmountField label="Project Price" value={i.projectPrice.amount} comment={i.projectPrice.comment} cur={cur}
        onChange={v => set('income.projectPrice.amount', v)} onCommentChange={v => set('income.projectPrice.comment', v)} />
      <AmountField label="Crew Expenses" value={i.crewExpenses.amount} comment={i.crewExpenses.comment} cur={cur} hint="auto in future"
        onChange={v => set('income.crewExpenses.amount', v)} onCommentChange={v => set('income.crewExpenses.comment', v)} />
      <AmountField label="Crew Overtimes" value={i.crewOvertimes.amount} comment={i.crewOvertimes.comment} cur={cur} hint="auto in future"
        onChange={v => set('income.crewOvertimes.amount', v)} onCommentChange={v => set('income.crewOvertimes.comment', v)} />
      <FinesList fines={i.fines} cur={cur} onChange={v => set('income.fines', v)} />
      <TotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      {/* Payments from Client */}
      <FinanceDivider label="Payments from Client" />
      <PaidList payments={data.clientPayments} cur={cur}
        onChange={v => onChange(prev => ({ ...prev, clientPayments: v }))} />
      <TotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Payments to Freelancers */}
      <FinanceDivider label="Payments to Freelancers" />
      <AmountField label="Planned Payment for Freelancers" value={fp.planned.amount} comment={fp.planned.comment} cur={cur}
        onChange={v => set('freelancerPayments.planned.amount', v)} onCommentChange={v => set('freelancerPayments.planned.comment', v)} />
      <AmountField label="Crew Expenses" value={fp.crewExpenses.amount} comment={fp.crewExpenses.comment} cur={cur}
        onChange={v => set('freelancerPayments.crewExpenses.amount', v)} onCommentChange={v => set('freelancerPayments.crewExpenses.comment', v)} />
      <AmountField label="Crew Overtimes" value={fp.crewOvertimes.amount} comment={fp.crewOvertimes.comment} cur={cur}
        onChange={v => set('freelancerPayments.crewOvertimes.amount', v)} onCommentChange={v => set('freelancerPayments.crewOvertimes.comment', v)} />
      <FinesList fines={fp.fines} cur={cur} onChange={v => set('freelancerPayments.fines', v)} />
      <TotalRow label="Total Payments to Freelancers" value={totalFreelancer} cur={cur} color="var(--warning-text)" />
      <PaidList payments={fp.paid} cur={cur} label="Already Paid to Freelancers"
        onChange={v => set('freelancerPayments.paid', v)} />
      <TotalRow label="To be Paid to Freelancers" value={freelancerToBe} cur={cur}
        color={freelancerToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Profit */}
      <FinanceDivider label="Profit" />
      <TotalRow label="PROFIT" value={profit} cur={cur} color={profit >= 0 ? 'var(--success-text)' : 'var(--accent)'} />
    </>
  );
}

function DryHireFinance({ data, onChange, cur }) {
  const set = makeUpdater(onChange);
  const i   = data.income;
  const sp  = data.supplierPayments;

  const totalPrice     = n(i.projectPrice.amount) + n(i.projectExpenses.amount) + n(i.paymentForDamages.amount) - sumFines(i.fines);
  const clientPaid     = sumPayments(data.clientPayments);
  const clientToBePaid = totalPrice - clientPaid;
  const totalSupplier  = n(sp.planned.amount) + n(sp.paymentForDamages.amount) - sumFines(sp.fines);
  const supplierPaid   = sumPayments(sp.paid);
  const supplierToBe   = totalSupplier - supplierPaid;
  const profit         = totalPrice - totalSupplier;

  return (
    <>
      {/* Income */}
      <FinanceDivider label="Income" />
      <AmountField label="Project Price" value={i.projectPrice.amount} comment={i.projectPrice.comment} cur={cur}
        onChange={v => set('income.projectPrice.amount', v)} onCommentChange={v => set('income.projectPrice.comment', v)} />
      <AmountField label="Project Expenses" value={i.projectExpenses.amount} comment={i.projectExpenses.comment} cur={cur}
        onChange={v => set('income.projectExpenses.amount', v)} onCommentChange={v => set('income.projectExpenses.comment', v)} />
      <AmountField label="Payment for Damages" value={i.paymentForDamages.amount} comment={i.paymentForDamages.comment} cur={cur}
        onChange={v => set('income.paymentForDamages.amount', v)} onCommentChange={v => set('income.paymentForDamages.comment', v)} />
      <FinesList fines={i.fines} cur={cur} onChange={v => set('income.fines', v)} />
      <TotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      {/* Payments from Client */}
      <FinanceDivider label="Payments from Client" />
      <PaidList payments={data.clientPayments} cur={cur}
        onChange={v => onChange(prev => ({ ...prev, clientPayments: v }))} />
      <TotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Payments to Suppliers */}
      <FinanceDivider label="Payments to Suppliers" />
      <AmountField label="Planned Payment for Suppliers" value={sp.planned.amount} comment={sp.planned.comment} cur={cur}
        onChange={v => set('supplierPayments.planned.amount', v)} onCommentChange={v => set('supplierPayments.planned.comment', v)} />
      <FinesList fines={sp.fines} cur={cur} onChange={v => set('supplierPayments.fines', v)} />
      <AmountField label="Payment for Damages" value={sp.paymentForDamages.amount} comment={sp.paymentForDamages.comment} cur={cur}
        onChange={v => set('supplierPayments.paymentForDamages.amount', v)} onCommentChange={v => set('supplierPayments.paymentForDamages.comment', v)} />
      <TotalRow label="Total Payments to Suppliers" value={totalSupplier} cur={cur} color="var(--warning-text)" />
      <PaidList payments={sp.paid} cur={cur} label="Already Paid to Suppliers"
        onChange={v => set('supplierPayments.paid', v)} />
      <TotalRow label="To be Paid to Suppliers" value={supplierToBe} cur={cur}
        color={supplierToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Profit */}
      <FinanceDivider label="Profit" />
      <TotalRow label="PROFIT" value={profit} cur={cur} color={profit >= 0 ? 'var(--success-text)' : 'var(--accent)'} />
    </>
  );
}

function PermanentInstallationFinance({ data, onChange, cur }) {
  const set = makeUpdater(onChange);
  const i   = data.income;
  const sp  = data.supplierPayments;
  const fp  = data.freelancerPayments;

  const totalPrice     = n(i.installationCost.amount) + n(i.equipmentCost.amount) - sumFines(i.fines);
  const clientPaid     = sumPayments(data.clientPayments);
  const clientToBePaid = totalPrice - clientPaid;

  const totalSupplier  = n(sp.planned.amount) + n(sp.paymentForDamages.amount) - sumFines(sp.fines);
  const supplierPaid   = sumPayments(sp.paid);
  const supplierToBe   = totalSupplier - supplierPaid;

  const totalFreelancer = n(fp.planned.amount) - sumFines(fp.fines);
  const freelancerPaid  = sumPayments(fp.paid);
  const freelancerToBe  = totalFreelancer - freelancerPaid;

  const profit = totalPrice - totalSupplier - totalFreelancer;

  return (
    <>
      {/* Income */}
      <FinanceDivider label="Income" />
      <AmountField label="Installation Cost" value={i.installationCost.amount} comment={i.installationCost.comment} cur={cur}
        onChange={v => set('income.installationCost.amount', v)} onCommentChange={v => set('income.installationCost.comment', v)} />
      <AmountField label="Equipment Cost" value={i.equipmentCost.amount} comment={i.equipmentCost.comment} cur={cur}
        onChange={v => set('income.equipmentCost.amount', v)} onCommentChange={v => set('income.equipmentCost.comment', v)} />
      <FinesList fines={i.fines} cur={cur} onChange={v => set('income.fines', v)} />
      <TotalRow label="Total Price" value={totalPrice} cur={cur} color="var(--info-text)" />

      {/* Payments from Client */}
      <FinanceDivider label="Payments from Client" />
      <PaidList payments={data.clientPayments} cur={cur}
        onChange={v => onChange(prev => ({ ...prev, clientPayments: v }))} />
      <TotalRow label="To be Paid by Client" value={clientToBePaid} cur={cur}
        color={clientToBePaid <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Payments to Suppliers */}
      <FinanceDivider label="Payments to Suppliers (Equipment)" />
      <AmountField label="Planned Payment for Suppliers" value={sp.planned.amount} comment={sp.planned.comment} cur={cur}
        onChange={v => set('supplierPayments.planned.amount', v)} onCommentChange={v => set('supplierPayments.planned.comment', v)} />
      <AmountField label="Payment for Damages" value={sp.paymentForDamages.amount} comment={sp.paymentForDamages.comment} cur={cur}
        onChange={v => set('supplierPayments.paymentForDamages.amount', v)} onCommentChange={v => set('supplierPayments.paymentForDamages.comment', v)} />
      <FinesList fines={sp.fines} cur={cur} onChange={v => set('supplierPayments.fines', v)} />
      <TotalRow label="Total Payments to Suppliers" value={totalSupplier} cur={cur} color="var(--warning-text)" />
      <PaidList payments={sp.paid} cur={cur} label="Already Paid to Suppliers"
        onChange={v => set('supplierPayments.paid', v)} />
      <TotalRow label="To be Paid to Suppliers" value={supplierToBe} cur={cur}
        color={supplierToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Payments to Freelancers */}
      <FinanceDivider label="Payments to Freelancers (Installation)" />
      <AmountField label="Planned Payment for Freelancers" value={fp.planned.amount} comment={fp.planned.comment} cur={cur}
        onChange={v => set('freelancerPayments.planned.amount', v)} onCommentChange={v => set('freelancerPayments.planned.comment', v)} />
      <FinesList fines={fp.fines} cur={cur} onChange={v => set('freelancerPayments.fines', v)} />
      <TotalRow label="Total Payments to Freelancers" value={totalFreelancer} cur={cur} color="var(--warning-text)" />
      <PaidList payments={fp.paid} cur={cur} label="Already Paid to Freelancers"
        onChange={v => set('freelancerPayments.paid', v)} />
      <TotalRow label="To be Paid to Freelancers" value={freelancerToBe} cur={cur}
        color={freelancerToBe <= 0 ? 'var(--success-text)' : 'var(--warning-text)'} />

      {/* Profit */}
      <FinanceDivider label="Profit" />
      <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 18px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '6px' }}>
          <span>Total Price</span><span style={{ color: 'var(--info-text)' }}>{cur} {totalPrice.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '6px' }}>
          <span>− Suppliers</span><span style={{ color: 'var(--warning-text)' }}>{cur} {totalSupplier.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '10px' }}>
          <span>− Freelancers</span><span style={{ color: 'var(--warning-text)' }}>{cur} {totalFreelancer.toLocaleString()}</span>
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>PROFIT</span>
          <span style={{ color: profit >= 0 ? 'var(--success-text)' : 'var(--accent)', fontSize: '22px', fontWeight: 'bold' }}>{cur} {profit.toLocaleString()}</span>
        </div>
      </div>
    </>
  );
}

// ─── Form helpers ──────────────────────────────────────────────────────────────

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{label}</label>
        {hint && <span style={{ color: 'var(--text-disabled)', fontSize: '10px', fontStyle: 'italic' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function AssignedFreelancersBlock({ assigned, onChange, freelancers = [], projectType = '' }) {
  const [search, setSearch]   = useState('');
  const [dropOpen, setDropOpen] = useState(false);
  const [dropPos, setDropPos]  = useState({ top: 0, left: 0, width: 300 });
  const inputRef = useRef(null);

  // assigned = [{ id, name, dateFrom, dateTo, _key }] — ПЛОСКИЙ список
  // проміжків участі. Той самий фрілансер (id) може повторюватись кількома
  // записами з різними датами — перерва в участі (напр. приїхав на тиждень,
  // поїхав, повернувся ще на тиждень пізніше). _key — стабільний клієнтський
  // ідентифікатор саме ЗАПИСУ (проміжку), бо id фрілансера більше не
  // унікальний в межах масиву; у вже збережених проєктах (створених до цієї
  // фічі) записи його не мають — тоді як ключ береться id (там гарантовано
  // рівно один запис на фрілансера).
  const addedIds = new Set(assigned.map(a => a.id));
  const genKey   = () => `k${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const available = freelancers
    .filter(f => f.status === 'active' || f.status === 'Active')
    .filter(f => !addedIds.has(f.id))
    .filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  const openDrop = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setDropOpen(true);
  };

  const add = f => {
    onChange([...assigned, { id: f.id, name: f.name, dateFrom: '', dateTo: '', role: '', _key: genKey(), initialStatus: 'pending' }]);
    setSearch('');
    setDropOpen(false);
  };

  // Додати ще один проміжок для ВЖЕ призначеного фрілансера (перерва в участі).
  const addPeriod = (id, name) => onChange([...assigned, { id, name, dateFrom: '', dateTo: '', role: '', _key: genKey() }]);

  const removePeriod     = key => onChange(assigned.filter(a => (a._key || a.id) !== key));
  const removeFreelancer = id  => onChange(assigned.filter(a => a.id !== id));

  const update = (key, field, val) =>
    onChange(assigned.map(a => (a._key || a.id) === key ? { ...a, [field]: val } : a));

  // Явні travel-дні в межах періоду (лише Crewing) — за замовчуванням
  // ПОРОЖНЬО (усі дні робочі), адмін сам позначає конкретні дати (04.08.2026).
  const toggleTravelDay = (key, dateStr) => {
    const period = assigned.find(a => (a._key || a.id) === key);
    const cur = new Set(period?.travelDates || []);
    cur.has(dateStr) ? cur.delete(dateStr) : cur.add(dateStr);
    update(key, 'travelDates', [...cur].sort());
  };

  // Перемикач Planning/Confirmed (п.5-6) — лише для ЩОЙНО доданих фрілансерів
  // (ще нема реального confirmationStatus з бекенду). Задає початковий стан
  // готовності: Planning → фрілансер сам підтверджує "Я готовий"; Confirmed →
  // готовність вважається підтвердженою одразу, фрілансер лише отримує лист.
  const setInitialStatus = (id, status) =>
    onChange(assigned.map(a => a.id === id ? { ...a, initialStatus: status } : a));

  const avatar = name => (
    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-accent)', fontSize: '11px', fontWeight: 'bold', flexShrink: 0 }}>
      {name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
    </div>
  );

  // Групуємо плоский список у картки — одна на фрілансера, всередині всі
  // його проміжки. Порядок карток = порядок першої появи фрілансера.
  const groups = [];
  assigned.forEach(a => {
    let g = groups.find(g => g.id === a.id);
    if (!g) {
      g = {
        id: a.id, name: a.name, periods: [],
        // confirmationStatus присутній лише якщо запис прийшов з бекенду
        // (фрілансер вже мав шанс відповісти) — тоді перемикач ховаємо і
        // показуємо реальний статус як бейдж. initialStatus — чорновий вибір
        // адміна для ЩЕ не збереженого призначення.
        confirmationStatus: a.confirmationStatus || null,
        initialStatus: a.initialStatus || 'pending',
      };
      groups.push(g);
    }
    g.periods.push(a);
  });

  // Попередження (не блокування — обрано свідомо: перетин може бути
  // навмисний, напр. дві різні ролі одночасно) при перетині проміжків.
  const hasOverlap = periods => {
    const withDates = periods.filter(p => p.dateFrom && p.dateTo);
    for (let i = 0; i < withDates.length; i++) {
      for (let j = i + 1; j < withDates.length; j++) {
        if (withDates[i].dateFrom <= withDates[j].dateTo && withDates[j].dateFrom <= withDates[i].dateTo) return true;
      }
    }
    return false;
  };

  return (
    <div>
      {/* Список доданих, згруповано по фрілансеру */}
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {groups.map(g => {
            const overlap = hasOverlap(g.periods);
            return (
              <div key={g.id} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {avatar(g.name)}
                    <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px' }}>{g.name}</span>
                    {g.periods.length > 1 && (
                      <span style={{ background: 'var(--bg-subtle)', color: 'var(--text-dim)', borderRadius: '10px', padding: '1px 8px', fontSize: '10px' }}>
                        {g.periods.length} periods
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {g.confirmationStatus ? (
                      <span style={{
                        fontSize: '10px', fontWeight: 'bold', padding: '3px 9px', borderRadius: '10px',
                        background: g.confirmationStatus === 'confirmed' ? 'var(--success-bg)'
                          : g.confirmationStatus === 'declined' ? 'var(--danger-bg)' : 'var(--warning-bg)',
                        color: g.confirmationStatus === 'confirmed' ? 'var(--success-text)'
                          : g.confirmationStatus === 'declined' ? 'var(--danger-text-soft)' : 'var(--warning-text)',
                      }}>
                        {g.confirmationStatus === 'confirmed' ? '✓ Ready' : g.confirmationStatus === 'declined' ? '✕ Declined' : '⏳ Awaiting readiness'}
                      </span>
                    ) : (
                      <div style={{ display: 'flex', border: '1px solid var(--border-strong)', borderRadius: '6px', overflow: 'hidden' }}>
                        {['pending', 'confirmed'].map(s => (
                          <button
                            key={s} type="button"
                            onClick={() => setInitialStatus(g.id, s)}
                            title={s === 'pending' ? 'Freelancer must confirm readiness themselves' : 'Freelancer is pre-confirmed, just gets notified'}
                            style={{
                              border: 'none', padding: '4px 10px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer',
                              background: g.initialStatus === s ? 'var(--accent)' : 'transparent',
                              color: g.initialStatus === s ? 'var(--on-accent)' : 'var(--text-dim)',
                            }}>
                            {s === 'pending' ? 'Planning' : 'Confirmed'}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFreelancer(g.id)}
                      title="Remove freelancer (all periods)"
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>
                      ×
                    </button>
                  </div>
                </div>

                {overlap && (
                  <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', color: 'var(--warning-text)', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', marginBottom: '10px' }}>
                    ⚠ Some periods below overlap — double-check the dates.
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {g.periods.map((p, idx) => {
                    const key = p._key || p.id;
                    const multi = g.periods.length > 1;
                    return (
                      <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', flex: 1 }}>
                            <div>
                              <label style={{ ...labelStyle, fontSize: '10px', marginBottom: '4px' }}>{multi ? `Period ${idx + 1} — From` : 'Date From'}</label>
                              <DatePicker
                                selected={p.dateFrom ? new Date(p.dateFrom) : null}
                                onChange={date => update(key, 'dateFrom', toDateStr(date))}
                                dateFormat="dd MMM yyyy" placeholderText="Start date..."
                                customInput={<input style={inputStyle} />}
                              />
                            </div>
                            <div>
                              <label style={{ ...labelStyle, fontSize: '10px', marginBottom: '4px' }}>{multi ? `Period ${idx + 1} — To` : 'Date To'}</label>
                              <DatePicker
                                selected={p.dateTo ? new Date(p.dateTo) : null}
                                onChange={date => update(key, 'dateTo', toDateStr(date))}
                                dateFormat="dd MMM yyyy" placeholderText="End date..."
                                minDate={p.dateFrom ? new Date(p.dateFrom) : null}
                                customInput={<input style={inputStyle} />}
                              />
                            </div>
                          </div>
                          {multi && (
                            <button
                              type="button"
                              onClick={() => removePeriod(key)}
                              title="Remove this period"
                              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', borderRadius: '6px', padding: '9px 10px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>
                              ✕
                            </button>
                          )}
                        </div>
                        <div>
                          <label style={{ ...labelStyle, fontSize: '10px', marginBottom: '4px' }}>{multi ? `Period ${idx + 1} — Role` : 'Role'}</label>
                          <input
                            style={inputStyle}
                            placeholder="e.g. Lighting Crew Chief"
                            maxLength={80}
                            value={p.role || ''}
                            onChange={e => update(key, 'role', e.target.value)}
                          />
                        </div>

                        {projectType === 'Crewing' && p.dateFrom && p.dateTo && (
                          <div>
                            <label style={{ ...labelStyle, fontSize: '10px', marginBottom: '4px' }}>
                              {multi ? `Period ${idx + 1} — ` : ''}Travel days <span style={{ fontWeight: 'normal', color: 'var(--text-disabled)' }}>(click a day to mark it as travel — rest are working days)</span>
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {datesInRange(p.dateFrom, p.dateTo).map(dateStr => {
                                const isTravel = (p.travelDates || []).includes(dateStr);
                                const d = new Date(dateStr + 'T00:00:00');
                                return (
                                  <button
                                    key={dateStr} type="button"
                                    onClick={() => toggleTravelDay(key, dateStr)}
                                    title={isTravel ? `${dateStr} — travel day (click to mark as working)` : `${dateStr} — working day (click to mark as travel)`}
                                    style={{
                                      border: `1px solid ${isTravel ? 'var(--warning-text)' : 'var(--border-strong)'}`,
                                      background: isTravel ? 'var(--warning-bg)' : 'transparent',
                                      color: isTravel ? 'var(--warning-text)' : 'var(--text-dim)',
                                      borderRadius: '6px', padding: '4px 6px', cursor: 'pointer',
                                      fontSize: '10px', lineHeight: 1.2, minWidth: '38px', textAlign: 'center',
                                    }}>
                                    {isTravel ? '✈️ ' : ''}{WEEKDAY_SHORT[d.getDay()]}<br />{d.getDate()}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => addPeriod(g.id, g.name)}
                  style={{ background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-dim)', padding: '7px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', width: '100%', marginTop: '10px' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-dim)'; }}>
                  + Add period (break in participation)
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Пошук і додавання */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          style={{ ...inputStyle, paddingLeft: '34px' }}
          placeholder="Search and add freelancer..."
          value={search}
          onChange={e => { setSearch(e.target.value); openDrop(); }}
          onFocus={openDrop}
        />
        <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '16px', pointerEvents: 'none' }}>+</span>

        {dropOpen && ReactDOM.createPortal(
          <>
            <div onClick={() => setDropOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 990 }} />
            <div style={{
              position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
              background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '8px',
              zIndex: 1000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
              maxHeight: '220px', overflowY: 'auto',
            }}>
              {available.length === 0 ? (
                <div style={{ padding: '14px 16px', color: 'var(--text-dim)', fontSize: '13px' }}>
                  {freelancers.length === 0 ? 'No freelancers in database' : 'No matches'}
                </div>
              ) : available.map(f => (
                <div
                  key={f.id}
                  onClick={() => add(f)}
                  style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {avatar(f.name)}
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold' }}>{f.name}</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{f.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </>,
          document.body
        )}
      </div>

      {assigned.length > 0 && (
        <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '8px' }}>
          {groups.length} freelancer{groups.length !== 1 ? 's' : ''} assigned
          {assigned.length !== groups.length ? ` · ${assigned.length} periods total` : ''}
        </div>
      )}
    </div>
  );
}

function ResponsiblePersons({ persons, onChange, companyContacts = [], companySelected = false }) {
  const update = (idx, field, val) => onChange(persons.map((p, i) => i === idx ? { ...p, [field]: val } : p));
  const add    = () => { if (persons.length < 10) onChange([...persons, emptyPerson()]); };
  const remove = idx => {
    const rest = persons.filter((_, i) => i !== idx);
    onChange(rest.length ? rest : [emptyPerson()]); // остання видалена — лишити порожню картку
  };

  // Контакти компанії, яких ще немає у списку (за імʼям+email)
  const availableContacts = companyContacts.filter(c =>
    (c.name || '').trim() &&
    !persons.some(p => p.name === c.name && (p.email || '') === (c.email || ''))
  );

  const addFromCompany = e => {
    const c = availableContacts[Number(e.target.value)];
    if (!c || persons.length >= 10) return;
    const person = { role: c.role || '', name: c.name || '', phone: c.phone || '', email: c.email || '', fromCompany: true };
    // Якщо єдина картка порожня — заповнити її, а не додавати нову
    const onlyEmpty = persons.length === 1 && !persons[0].name && !persons[0].email && !persons[0].phone;
    onChange(onlyEmpty ? [person] : [...persons, person]);
    e.target.value = '';
  };

  return (
    <div>
      {/* Вибір зі співробітників компанії */}
      {availableContacts.length > 0 && persons.length < 10 && (
        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Add from company contacts</label>
          <select style={inputStyle} defaultValue="" onChange={addFromCompany}>
            <option value="">Select a contact person...</option>
            {availableContacts.map((c, i) => (
              <option key={c.id ?? i} value={i}>
                {c.name}{c.role ? ` — ${c.role}` : ''}{c.email ? ` (${c.email})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {companySelected && companyContacts.length === 0 && (
        <div style={{ color: 'var(--text-disabled)', fontSize: '12px', marginBottom: '12px' }}>
          У обраної компанії немає збережених контактів — додай особу вручну нижче.
        </div>
      )}
      {persons.map((person, idx) => (
        <div key={idx} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Person {idx + 1}</span>
            {(persons.length > 1 || person.name || person.email || person.phone || person.role) && (
              <span onClick={() => remove(idx)} style={{ color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' }}>Remove</span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Role</label>
              {person.fromCompany ? (
                // Особа з картки компанії — роль береться звідти, без вибору
                <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', cursor: 'default' }}>
                  {person.role || '—'}
                </div>
              ) : (
                <>
                  <select style={inputStyle} value={PERSON_ROLES.includes(person.role) ? person.role : (person.role ? 'Other' : '')} onChange={e => update(idx, 'role', e.target.value)}>
                    <option value="">Select role...</option>
                    {PERSON_ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                  {(!PERSON_ROLES.includes(person.role) && person.role) && (
                    <input style={{ ...inputStyle, marginTop: '8px' }} placeholder="Specify role..." value={person.role} onChange={e => update(idx, 'role', e.target.value)} />
                  )}
                </>
              )}
            </div>
            <div>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} placeholder="Full name" value={person.name} onChange={e => update(idx, 'name', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle} placeholder="+1 234 567 890" value={person.phone} onChange={e => update(idx, 'phone', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} placeholder="email@company.com" value={person.email} onChange={e => update(idx, 'email', e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={!!person.visibleToFreelancers}
              onChange={e => update(idx, 'visibleToFreelancers', e.target.checked)}
              style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: 'var(--accent)' }}
            />
            <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Visible to freelancers assigned to this project</span>
          </label>
        </div>
      ))}
      {persons.length < 10 && (
        <button onClick={add}
          style={{ background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-dim)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', width: '100%' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-dim)'; }}>
          + Add Person ({persons.length}/10)
        </button>
      )}
    </div>
  );
}

// ─── Assigned Admins (для всіх типів проєктів) ────────────────────────────────
// Адмін бачить лише проєкти, де він вказаний у Assigned.

function AssignedAdminsBlock({ assigned, onChange, adminUsers = [] }) {
  const admins = assigned.filter(a => a.isAdmin);
  const available = adminUsers.filter(u => !admins.some(a => a.userId === u.id));

  const add = e => {
    const u = available[Number(e.target.value)];
    if (!u) return;
    onChange([...assigned, { id: `admin-${u.id}`, userId: u.id, name: u.name, isAdmin: true }]);
    e.target.value = '';
  };

  const remove = userId => onChange(assigned.filter(a => !(a.isAdmin && a.userId === userId)));

  return (
    <div>
      {available.length > 0 && (
        <select style={{ ...inputStyle, marginBottom: admins.length ? '12px' : 0 }} defaultValue="" onChange={add}>
          <option value="">Add admin...</option>
          {available.map((u, i) => (
            <option key={u.id} value={i}>{u.name}{u.role === 'SuperAdmin' ? ' (Super Admin)' : ''} — {u.email}</option>
          ))}
        </select>
      )}
      {admins.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {admins.map(a => (
            <span key={a.userId} style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', color: 'var(--info-text)', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              🛡 {a.name}
              <span onClick={() => remove(a.userId)} style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 'bold' }}>×</span>
            </span>
          ))}
        </div>
      )}
      {admins.length === 0 && (
        <div style={{ color: 'var(--text-disabled)', fontSize: '12px', marginTop: '8px' }}>
          Нікого не призначено. Звичайні адміни бачать лише проєкти, де вони вказані.
        </div>
      )}
    </div>
  );
}

// ─── Project Equipment (Dry Hire) ─────────────────────────────────────────────

function ProjectEquipment({ items, onChange, stock = [], usageMap = {} }) {
  const update = (idx, field, val) => onChange(items.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const remove = idx => onChange(items.filter((_, i) => i !== idx));

  // Доступно = склад мінус резерв (Active/Confirmed) мінус те, що в сервісі
  const freeQty = s => s.quantity - (usageMap[s.id]?.reserved || 0) - serviceQty(s);

  // Позиції зі складу, яких ще немає у списку
  const available = stock.filter(s => !items.some(it => it.equipmentId === s.id));

  const addFromStock = e => {
    const s = available[Number(e.target.value)];
    if (!s) return;
    onChange([...items, { equipmentId: s.id, name: s.name, category: getCats(s).join(' / '), unit: unitOf(s), qty: 1 }]);
    e.target.value = '';
  };

  const addManual = () => onChange([...items, { equipmentId: null, name: '', category: '', qty: 1 }]);

  return (
    <div>
      {available.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Add from equipment stock</label>
          <select style={inputStyle} defaultValue="" onChange={addFromStock}>
            <option value="">Select equipment...</option>
            {available.map((s, i) => (
              <option key={s.id} value={i}>
                {s.name}{getCats(s).length ? ` [${getCats(s).join('/')}]` : ''} — available: {freeQty(s)} of {s.quantity} {unitOf(s)}
              </option>
            ))}
          </select>
        </div>
      )}

      {items.map((it, idx) => {
        const stockItem = it.equipmentId ? stock.find(s => s.id === it.equipmentId) : null;
        const overStock = stockItem && Number(it.qty) > freeQty(stockItem);
        return (
          <div key={idx} style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '10px', display: 'grid', gridTemplateColumns: '1fr 120px 90px', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>Equipment</label>
              {it.equipmentId ? (
                <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: 'var(--text-body)', cursor: 'default' }}>
                  {it.name}{it.category ? <span style={{ color: 'var(--text-dim)', marginLeft: '8px', fontSize: '11px' }}>[{it.category}]</span> : null}
                </div>
              ) : (
                <input style={inputStyle} placeholder="Equipment name" value={it.name} onChange={e => update(idx, 'name', e.target.value)} />
              )}
            </div>
            <div>
              <label style={labelStyle}>Quantity{it.unit ? `, ${it.unit}` : ''}</label>
              <input
                style={{ ...inputStyle, borderColor: overStock ? 'var(--accent)' : 'var(--border-strong)' }}
                type="number" min="1"
                value={it.qty}
                onChange={e => update(idx, 'qty', e.target.value)}
              />
              {overStock && <div style={{ color: 'var(--accent)', fontSize: '10px', marginTop: '4px' }}>Available: {freeQty(stockItem)} {unitOf(stockItem)} (reserved on active projects)</div>}
            </div>
            <button type="button" onClick={() => remove(idx)}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--accent)', padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
              Remove
            </button>
          </div>
        );
      })}

      <button onClick={addManual}
        style={{ background: 'transparent', border: '1px dashed var(--border-strong)', color: 'var(--text-dim)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', width: '100%' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-dim)'; }}>
        + Add manually
      </button>

      {stock.length === 0 && (
        <div style={{ color: 'var(--text-disabled)', fontSize: '12px', marginTop: '8px' }}>
          Склад порожній — наповни сторінку Equipment, щоб обирати обладнання зі списку.
        </div>
      )}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

function ProjectForm({ onBack, initialData = null, onSave, companies: companiesProp = [], freelancers: freelancersProp = [], equipment: equipmentProp = [], projects: projectsProp = [], adminUsers = [], currentUser = null }) {
  const isEdit = !!initialData;

  const [form, setForm] = useState({
    name:        initialData?.name        || '',
    company:     initialData?.company     || '',
    type:        initialData?.type        || '',
    location:    initialData?.location    || '',
    startDate:   initialData?.startDate   || '',
    endDate:     initialData?.endDate     || '',
    status:        initialData?.status        || 'Planning',
    paymentStatus: Array.isArray(initialData?.paymentStatus) ? initialData.paymentStatus : [],
    assigned:    (() => {
      const list = (initialData?.assigned || []).map(a =>
        typeof a === 'string'
          ? { id: `legacy-${a}`, name: a, dateFrom: '', dateTo: '' }
          : a
      );
      // Новий проєкт від звичайного адміна — одразу додати його самого,
      // інакше проєкт зникне з його видимості після збереження
      if (!initialData && currentUser?.role === 'Admin' && !list.some(a => a.isAdmin && a.userId === currentUser.id)) {
        list.push({ id: `admin-${currentUser.id}`, userId: currentUser.id, name: currentUser.name, isAdmin: true });
      }
      return list;
    })(),
    persons:     (initialData?.persons?.length ? initialData.persons : [emptyPerson()]),
    equipment:   initialData?.equipment   || [],
    docFolders:  (initialData?.docFolders?.length
      ? initialData.docFolders
      : buildDefaultDocFolders((initialData?.assigned || []).map(a => typeof a === 'string' ? a : a.name))),
    description: initialData?.description || '',
    // Показується фрілансеру в кабінеті ЗАМІСТЬ внутрішнього description
    // (там можуть бути фінансові/клієнтські деталі, не для його очей).
    descriptionForFreelancers: initialData?.descriptionForFreelancers || '',
    finance: {
      currency:    initialData?.finance?.currency    || 'EUR',
      crewing:     initialData?.finance?.crewing     || defaultCrewingFinance(),
      dryHire:     initialData?.finance?.dryHire     || defaultDryHireFinance(),
      permInstall: initialData?.finance?.permInstall || defaultPermInstallFinance(),
    },
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const setCurrency = cur => setForm(prev => ({ ...prev, finance: { ...prev.finance, currency: cur } }));

  const setCrewingFinance = updater => setForm(prev => ({
    ...prev,
    finance: { ...prev.finance, crewing: typeof updater === 'function' ? updater(prev.finance.crewing) : updater },
  }));

  const setDryHireFinance = updater => setForm(prev => ({
    ...prev,
    finance: { ...prev.finance, dryHire: typeof updater === 'function' ? updater(prev.finance.dryHire) : updater },
  }));

  const setPermInstallFinance = updater => setForm(prev => ({
    ...prev,
    finance: { ...prev.finance, permInstall: typeof updater === 'function' ? updater(prev.finance.permInstall) : updater },
  }));

  const cur = form.finance.currency;

  const handleSubmit = () => {
    if (!form.name.trim()) { alert('Project name is required'); return; }
    const { income, outcome, profit } = computeFinanceSummary(form.finance, form.type);
    const payload = {
      ...form,
      id:       initialData?.id       || Date.now(),
      archived: initialData?.archived || false,
      currency: form.finance.currency,   // валюта проєкту — верхньорівневе поле для БД
      income,
      outcome,
      profit,
      // Реальні (не-адмінські, не-legacy) freelancer-призначення для
      // синхронізації project_assignments на бекенді, разом з особистими
      // датами фрілансера на проєкті (можуть відрізнятись від дат проєкту) —
      // без цього призначений фрілансер ніколи не побачить проєкт у
      // "My Projects" / календарі (GET /api/projects/mine читає саме цю таблицю).
      assignments: form.assigned
        .filter(a => !a.isAdmin && typeof a.id === 'number')
        .map(a => ({
          freelancerId: a.id, dateFrom: a.dateFrom || null, dateTo: a.dateTo || null, role: a.role || null,
          travelDates: Array.isArray(a.travelDates) ? a.travelDates : [],
        })),
      // Початковий стан готовності (Planning/Confirmed перемикач, п.5-6) —
      // бекенд застосовує це лише для ЩЕ НЕ призначених фрілансерів
      // (ON CONFLICT DO NOTHING), тож дублікати чи вже відповілі фрілансери
      // тут нешкідливі.
      assignmentStatuses: Object.values(
        form.assigned
          .filter(a => !a.isAdmin && typeof a.id === 'number')
          .reduce((acc, a) => {
            acc[a.id] = { freelancerId: a.id, status: a.confirmationStatus === 'confirmed' || a.initialStatus === 'confirmed' ? 'confirmed' : 'pending' };
            return acc;
          }, {})
      ),
    };
    if (onSave) onSave(payload);
    onBack();
  };

  const renderFinance = () => {
    if (!form.type) return (
      <div style={{ textAlign: 'center', color: 'var(--text-disabled)', padding: '32px', fontSize: '13px', border: '1px dashed var(--bg-subtle)', borderRadius: '8px' }}>
        Select a project type first to configure the finance section
      </div>
    );
    return (
      <>
        <div style={{ maxWidth: '200px', marginBottom: '8px' }}>
          <label style={labelStyle}>Currency</label>
          <select style={inputStyle} value={cur} onChange={e => setCurrency(e.target.value)}>
            {FORM_CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        {form.type === 'Crewing'                && <CrewingFinance              data={form.finance.crewing}     onChange={setCrewingFinance}     cur={cur} />}
        {form.type === 'Dry hire'               && <DryHireFinance               data={form.finance.dryHire}     onChange={setDryHireFinance}     cur={cur} />}
        {form.type === 'Permanent installation' && <PermanentInstallationFinance data={form.finance.permInstall} onChange={setPermInstallFinance} cur={cur} />}
      </>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span onClick={onBack} style={{ color: 'var(--text-dim)', fontSize: '13px', cursor: 'pointer' }}>← Back</span>
          <span style={{ color: 'var(--border-strong)' }}>|</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>
            {isEdit ? 'Edit Project' : 'New Project'}
          </span>
          {form.type && (
            <span style={{ background: 'var(--border)', color: 'var(--text-muted)', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>{form.type}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px 24px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
            {isEdit ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>

        {/* General Info */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>General Information</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field label="Project Name">
              <input style={inputStyle} placeholder="Enter project name" value={form.name} onChange={e => set('name', e.target.value)} />
            </Field>
            <Field label="Company">
              <select style={inputStyle} value={form.company} onChange={e => set('company', e.target.value)}>
                <option value="">Select company...</option>
                {(companiesProp.length > 0 ? companiesProp.map(c => c.name) : mockCompanies).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </Field>
            <Field label="Project Type">
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="">Select type...</option>
                {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <input style={inputStyle} placeholder="City, Country" value={form.location} onChange={e => set('location', e.target.value)} />
            </Field>
            <Field label="Start Date">
              <DatePicker
                selected={form.startDate ? new Date(form.startDate) : null}
                onChange={date => set('startDate', toDateStr(date))}
                dateFormat="dd MMM yyyy" placeholderText="Select date..."
                customInput={<input style={inputStyle} />}
              />
            </Field>
            <Field label="End Date">
              <DatePicker
                selected={form.endDate ? new Date(form.endDate) : null}
                onChange={date => set('endDate', toDateStr(date))}
                dateFormat="dd MMM yyyy" placeholderText="Select date..."
                minDate={form.startDate ? new Date(form.startDate) : null}
                customInput={<input style={inputStyle} />}
              />
            </Field>
            <Field label="Status">
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Payment Status">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '4px' }}>
                {[...PAYMENT_STATUSES, ...(form.type === 'Dry hire' ? DRY_HIRE_PAYMENT_STATUSES : [])].map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-body)', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={form.paymentStatus.includes(s)}
                      onChange={() => set('paymentStatus', form.paymentStatus.includes(s)
                        ? form.paymentStatus.filter(x => x !== s)
                        : [...form.paymentStatus, s])}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Description">
            <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} placeholder="Project description..."
              value={form.description} onChange={e => set('description', e.target.value)} />
          </Field>
          <Field label="Description for freelancers">
            <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
              placeholder="What assigned freelancers see instead of the description above — leave empty to show nothing..."
              value={form.descriptionForFreelancers} onChange={e => set('descriptionForFreelancers', e.target.value)} />
            <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '4px' }}>
              Freelancers never see the internal Description above — only this text.
            </div>
          </Field>
        </div>

        {/* Assigned Admins — для всіх типів проєктів */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>Assigned Admins</p>
          <AssignedAdminsBlock
            assigned={form.assigned}
            onChange={val => set('assigned', val)}
            adminUsers={adminUsers}
          />
        </div>

        {/* Assigned Freelancers — тільки для Crewing і Permanent installation */}
        {(form.type === 'Crewing' || form.type === 'Permanent installation') && (
          <div style={sectionStyle}>
            <p style={sectionTitle}>Assigned Freelancers</p>
            <AssignedFreelancersBlock
              assigned={form.assigned.filter(a => !a.isAdmin)}
              onChange={val => set('assigned', [...form.assigned.filter(a => a.isAdmin), ...val])}
              freelancers={freelancersProp}
              projectType={form.type}
            />
          </div>
        )}

        {/* Equipment — тільки для Dry Hire */}
        {form.type === 'Dry hire' && (
          <div style={sectionStyle}>
            <p style={sectionTitle}>Equipment</p>
            <ProjectEquipment
              items={form.equipment}
              onChange={val => set('equipment', val)}
              stock={equipmentProp}
              usageMap={computeEquipmentUsage(projectsProp.filter(p => p.id !== initialData?.id))}
            />
          </div>
        )}

        {/* Client's Responsible Persons */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>Client's Responsible Persons</p>
          <ResponsiblePersons
            persons={form.persons}
            onChange={val => set('persons', val)}
            companyContacts={companiesProp.find(c => c.name === form.company)?.contacts || []}
            companySelected={!!form.company}
          />
        </div>

        {/* Finance */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>Finance</p>
          {renderFinance()}
        </div>

        {/* Documents */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>Documents</p>
          <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-text)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '15px' }}>ℹ️</span>
            <span style={{ color: 'var(--info-text)', fontSize: '12px', lineHeight: 1.5 }}>
              Files in <b>Project Documents</b> are visible to every freelancer assigned to this project.
              Each crew member's own subfolder under <b>Crew</b> is private to them — other freelancers can't see it.
            </span>
          </div>
          <DocumentsSection
            folders={form.docFolders}
            onChange={u => setForm(prev => ({ ...prev, docFolders: typeof u === 'function' ? u(prev.docFolders) : u }))}
            projectId={initialData?.id}
            assignedFreelancers={(form.assigned || []).map(a => typeof a === 'string' ? a : a.name)}
          />
        </div>

      </div>
    </div>
  );
}

export default ProjectForm;
